import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { U2bValidators } from '@shared/validators/validators';
import { Person } from '@shared/models/person';
import { MatDialog } from '@angular/material/dialog';
import { GetAddressDialogComponent } from '@shared/components';
import { takeUntil } from 'rxjs/operators';
import { Observable, Subject } from 'rxjs';
import { disableControlsByName } from '@shared/functions/form/disable-controls';
import { enableControlsByName } from '@shared/functions/form/enable-controls';
import { CompanyContact } from '@shared/models/company-contact';
import { TranslationService } from '@core/translation/translation.service';

@Component({
    selector: 'form-widget-company-contact',
    templateUrl: './form-widget-company-contact.component.html',
    standalone: false
})
export class FormWidgetCompanyContactComponent implements OnInit, OnDestroy {

    private _unsubscribeAll = new Subject<any>();

    /** When unset, template uses {@code workflows.formWidgets.companyContact.headline}. */
    @Input()
    headline: string | null = null;

    @Input()
    set parentFormGroup(value: UntypedFormGroup) {
        this._parentFormGroup = value;
        this.addFormGroupToParentFormGroup();
    }

    get parentFormGroup(): UntypedFormGroup {
        return this._parentFormGroup;
    }

    private _parentFormGroup: UntypedFormGroup;

    @Input()
    person$: Observable<Person>;

    @Input()
    companyContact: CompanyContact;

    companyContactFormGroup: UntypedFormGroup;


    constructor(private _formBuilder: UntypedFormBuilder,
                private _matDialog: MatDialog) {
    }

    ngOnInit(): void {

        this.companyContactFormGroup = this._formBuilder.group({
            person: [this.companyContact.person || null],
            personName: [{
                value: this.companyContact?.personName || null,
                disabled: !!this.companyContact.person
            }, [U2bValidators.required(TranslationService.translate('workflows.formWidgets.companyContact.nameRequired'))]],
            personMail: [{
                value: this.companyContact?.personMail || null,
                disabled: !!this.companyContact.person
            }, [U2bValidators.email()]],
            personPhone: [{
                value: this.companyContact?.personPhone || null,
                disabled: !!this.companyContact.person
            }, []]
        });

        this.companyContactFormGroup
            .get('person')
            .valueChanges
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((person) => {
                if (person && person.id) {
                    disableControlsByName(
                        this.companyContactFormGroup,
                        ['personName', 'personMail', 'personPhone']
                    );
                } else {
                    enableControlsByName(
                        this.companyContactFormGroup,
                        ['personName', 'personMail', 'personPhone'],
                        true
                    );
                }
            });

        this.addFormGroupToParentFormGroup();

    }

    ngOnDestroy(): void {
        this.parentFormGroup.removeControl('companyContactForm');
        this._unsubscribeAll.next(undefined);
        this._unsubscribeAll.complete();
    }

    private addFormGroupToParentFormGroup(): void {
        if (!this.parentFormGroup || !this.companyContactFormGroup) {
            return;
        }
        this.parentFormGroup.addControl('companyContactForm', this.companyContactFormGroup);
    }

    onClickAddAddress(): void {

        const dialogRef = this._matDialog.open(GetAddressDialogComponent);

        dialogRef
            .afterClosed()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((person: Person) => {
                if (!person) {
                    return;
                }

                this.companyContact = new CompanyContact();

                this.companyContact.personId = person.id;
                this.companyContact.person = person;
                this.companyContact.personName = person.fullName;
                this.companyContact.personPhone = person.phone;
                this.companyContact.personMail = person.mail;

                this.companyContactFormGroup.patchValue({...this.companyContact}, {onlySelf: true});

                this.companyContactFormGroup.markAsDirty();
            });
    }

    onClickRemovePerson(): void {
        this.companyContact = null;
        this.companyContactFormGroup.get('person').setValue(null);
        this.companyContactFormGroup.reset();
        this.companyContactFormGroup.enable();
        this.companyContactFormGroup.markAsDirty();
    }
}
